import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { invalidateLedgerAnalyticsCache, invalidateCollectionCache } from '../cacheInvalidation';
import type {
    Ledger,
    CreateLedgerData,
    UpdateLedgerData,
    PaginatedResponse,
    LedgerType,
    ArchiveOptions,
    ArchiveLedgerData,
} from '../types';

interface UseLedgersParams {
    type: LedgerType;
    collectionId?: number | null;
    includeArchived?: boolean;
}

export function useLedgers({ type, collectionId, includeArchived }: UseLedgersParams) {
    const params = new URLSearchParams();
    params.append('type', type);
    if (collectionId) params.append('collectionId', String(collectionId));
    if (includeArchived) params.append('includeArchived', 'true');
    // Load all ledgers - no limit parameter to allow backend to return all available ledgers

    return useQuery({
        queryKey: ['ledgers', { type, collectionId, includeArchived }],
        queryFn: () => api.get<PaginatedResponse<Ledger>>(`/api/ledgers?${params.toString()}`),
    });
}

export function useLedger(id: number) {
    return useQuery({
        queryKey: ['ledgers', id],
        queryFn: () => api.get<Ledger>(`/api/ledgers/${id}`),
        enabled: !!id,
    });
}

export function useCreateLedger() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateLedgerData) => api.post<Ledger>('/api/ledgers', data),
        onSuccess: () => {
            // Invalidate all ledgers queries to refetch with new data
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
    });
}

export function useUpdateLedger() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateLedgerData }) =>
            api.patch<Ledger>(`/api/ledgers/${id}`, data),
        onSuccess: (updatedLedger) => {
            // Invalidate list queries
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ledger detail batch (used by LedgerDetailPage)
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch', updatedLedger.id] });
            // Update individual ledger cache
            queryClient.setQueryData<Ledger>(['ledgers', updatedLedger.id], updatedLedger);
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
    });
}

export function useDeleteLedger() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete<void>(`/api/ledgers/${id}`),
        onSuccess: (_, deletedId) => {
            // Invalidate list queries
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Remove from individual cache
            queryClient.removeQueries({ queryKey: ['ledgers', deletedId] });
            // Remove ledger detail batch cache
            queryClient.removeQueries({ queryKey: ['ledger-detail-batch', deletedId] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
            // Invalidate collection cache so ledgerCount updates (enables collection deletion)
            invalidateCollectionCache(queryClient);
        },
    });
}

/**
 * Fetch archive options for a ledger.
 * Returns information needed to render the archive confirmation modal.
 */
export function useArchiveOptions(ledgerId: number, enabled = true) {
    return useQuery({
        queryKey: ['ledgers', ledgerId, 'archive-options'],
        queryFn: () => api.get<ArchiveOptions>(`/api/ledgers/${ledgerId}/archive-options`),
        enabled: enabled && !!ledgerId,
    });
}

export function useArchiveLedger() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data?: ArchiveLedgerData }) =>
            api.post<Ledger>(`/api/ledgers/${id}/archive`, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ledger detail batch (used by LedgerDetailPage)
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch', id] });
            // Invalidate archive options for this ledger
            queryClient.invalidateQueries({ queryKey: ['ledgers', id, 'archive-options'] });
            // Also invalidate ledger values since we may have added a zero-value entry
            queryClient.invalidateQueries({ queryKey: ['ledger-values', id] });
            // And returns since we may have added a withdrawal
            queryClient.invalidateQueries({ queryKey: ['ledger-returns', id] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
    });
}

export function useRestoreLedger() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.post<Ledger>(`/api/ledgers/${id}/restore`),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ledger detail batch (used by LedgerDetailPage)
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch', id] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
    });
}

export function useConfirmLedgerUpdate() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.post<Ledger>(`/api/ledgers/${id}/confirm-update`),
        onSuccess: (updatedLedger) => {
            // Invalidate list queries to show updated status
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ledger detail batch (used by LedgerDetailPage)
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch', updatedLedger.id] });
            // Update individual ledger cache
            queryClient.setQueryData<Ledger>(['ledgers', updatedLedger.id], updatedLedger);
            // Invalidate all analytics (confirmedUpdatedDate affects filtering & FX timestamps)
            invalidateLedgerAnalyticsCache(queryClient, updatedLedger.id);
        },
    });
}

export function useRevertConfirmedDate() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.post<Ledger>(`/api/ledgers/${id}/revert-confirmed-date`),
        onSuccess: (updatedLedger) => {
            // Invalidate list queries to show updated status
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ledger detail batch (used by LedgerDetailPage)
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch', updatedLedger.id] });
            // Update individual ledger cache
            queryClient.setQueryData<Ledger>(['ledgers', updatedLedger.id], updatedLedger);
            // Invalidate all analytics (confirmedUpdatedDate affects filtering & FX timestamps)
            invalidateLedgerAnalyticsCache(queryClient, updatedLedger.id);
        },
    });
}

/**
 * Reorder hook for drag-and-drop ledger reordering.
 * Local state in LedgerList keeps the item in place visually after drop.
 * On success, cache is invalidated and fresh data is fetched from server.
 *
 * @param includeArchived - Whether archived ledgers are visible in the current view.
 *                          This tells the backend to only shift weights for visible items.
 */
export function useReorderLedger() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            id,
            weight,
            includeArchived = false,
        }: {
            id: number;
            weight: number;
            includeArchived?: boolean;
        }) => api.patch<Ledger>(`/api/ledgers/${id}`, { weight, includeArchived }),

        onSettled: () => {
            // Refetch to get accurate weights from server
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
        },
    });
}
